import { GetServerSideProps } from 'next';
import ComparisonInterface from 'types/Apis/Comparison';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { fetchComparison } from 'lib/AutolistAPI';
import Layout from 'components/Layout';
import StructuredDataComponent from 'components/StructuredData';
import StructuredData from 'lib/StructuredData/Comparison';
import Error from 'pages/_error';
import Comparison from 'components/Comparison';
import Head from 'next/head';
import { COMPARISON_URL } from 'lib/ContentGroups';
import useAdSetup from 'lib/hooks/useAdSetup';

import { useContentGroups } from 'lib/hooks/useGoogleAnalytics';
import breadcrumbStructuredData from 'lib/StructuredData/breadcrumbStructuredData';
// eslint-disable-next-line import/no-unresolved
import { captureException } from '@sentry/nextjs';
import i18n from 'lib/i18next';
import config from 'lib/config';
import CarGurusAdUnit from 'lib/CarGurusAds/AdUnit';
import { AgeType, PageType, Zone } from 'lib/CarGurusAds/TaxonomySchema';
import { enableContentCaching } from 'lib/cacheControl';
import isIndexable from 'lib/isIndexable';

interface Props {
  comparison?: ComparisonInterface;
  statusCode?: number;
  indexable: boolean;
}

export default function ComparisonPage({ comparison, statusCode, indexable }: Props): JSX.Element {
  const { asPath } = useRouter();
  const { t } = useTranslation();
  useContentGroups({ default: COMPARISON_URL });

  const carGurusAdUnit = new CarGurusAdUnit({ zone: Zone.Research });

  useAdSetup(carGurusAdUnit.toString(), {
    make: null,
    model: null,
    year: null,
    pageType: PageType.Comparisons,
    ageType: AgeType.None,
    cpo: false,
    inLine: 1,
    body_style: null,
    category: null,
    app: 'web',
    sellerId: null,
  });

  if (!comparison || (statusCode && statusCode !== 200)) {
    return <Error statusCode={statusCode} />;
  }

  const shareUrl = new URL(`https://${config.host}`);
  if (asPath) shareUrl.pathname = asPath;

  const structuredData = new StructuredData({
    comparison,
    shareUrl,
  });

  return (
    <>
      <Layout
        title={comparison.title}
        description={t('comparison.description', {
          vehicle1: comparison.vehicle1,
          vehicle2: comparison.vehicle2,
        })}
        indexed={indexable}
      >
        <Head>
          <meta content={comparison.vehicle1.photo_url} property="og:image" key="og:image" />
          <meta content={'article'} property="og:type" key="og:type" />
        </Head>
        <Comparison shareUrl={shareUrl.toString()} comparison={comparison} />
      </Layout>
      <StructuredDataComponent data={structuredData} />
      <StructuredDataComponent
        data={breadcrumbStructuredData({
          url: shareUrl.toString(),
          title: comparison.title,
          breadcrumbCategory: 'guides',
        })}
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  req,
  res,
  locale = i18n.language,
}) => {
  const bypassCache = true;
  const indexable = isIndexable(req);
  let fetchedComparison;

  try {
    fetchedComparison = await fetchComparison(
      query.slug as string,
      {
        locale,
      },
      { bypassCache }
    );
  } catch (error) {
    // When there is an api error, we don't want the page to 500 because we
    // want graceful error handling on Autolist-web, the api has its own error
    // monitoring
    res.statusCode = 500;
    captureException(error);
  }

  const { comparison } = fetchedComparison || {};

  if (!comparison) {
    res.statusCode = 404;
  }

  enableContentCaching(res);

  return {
    props: {
      comparison,
      statusCode: res?.statusCode,
      indexable,
    },
  };
};
